import { useState, useEffect } from 'react';
import { FileText, AlertTriangle, TrendingUp, Globe, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import toast from 'react-hot-toast';

function OCRMetricsPanel() {
  const [metrics, setMetrics] = useState(null);
  const [failedExtractions, setFailedExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    fetchOCRMetrics();
  }, [timeRange]);

  const fetchOCRMetrics = async () => {
    try {
      setLoading(true);

      const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - daysAgo);

      // Fetch all image uploads in range
      const { data: uploads, error } = await supabase?.from('case_uploads')?.select('id, file_name, file_type, extracted_handwritten_text, uploaded_at, case_id')?.or('is_image.eq.true,file_type.ilike.image/%')?.gte('uploaded_at', startDate?.toISOString())?.order('uploaded_at', { ascending: false });

      if (error) throw error;

      const totalImages = uploads?.length || 0;

      // Processed = has extracted_handwritten_text (not null)
      // This now includes both "text found" and "no text (empty string)" results
      const processedUploads = uploads?.filter(u => u?.extracted_handwritten_text !== null) || [];
      const totalProcessed = processedUploads.length;
      const notProcessed = totalImages - totalProcessed;

      const ocrData = processedUploads.map(u => u?.extracted_handwritten_text);

      // Successful = text was actually extracted (non-empty)
      const withText = ocrData.filter(ocr => ocr?.text?.trim()?.length > 0);
      // Clean = processed but no handwriting found (correct result, not a failure)
      const noTextFound = ocrData.filter(ocr => !ocr?.text?.trim());

      const avgConfidence = withText.length > 0
        ? withText.reduce((sum, ocr) => sum + (ocr?.confidence || 0), 0) / withText.length
        : 0;

      const qualityDistribution = {
        good: ocrData.filter(ocr => ocr?.quality === 'good').length,
        fair: ocrData.filter(ocr => ocr?.quality === 'fair').length,
        poor: ocrData.filter(ocr => ocr?.quality === 'poor').length
      };

      const languageDistribution = {};
      withText.forEach(ocr => {
        const lang = ocr?.language || 'unknown';
        languageDistribution[lang] = (languageDistribution[lang] || 0) + 1;
      });

      const avgProcessingTime = ocrData.length > 0
        ? ocrData.reduce((sum, ocr) => sum + (ocr?.processing_time_ms || 0), 0) / ocrData.length
        : 0;

      // Identify failed extractions (processed but no useful result). 
      // NOTE: "No handwriting in image" is NOT a failure — it's a correct answer.
      // We only flag these for visibility, not as errors.
      const failed = uploads
        ?.filter(u =>
          u?.extracted_handwritten_text !== null &&
          (!u?.extracted_handwritten_text?.text || u?.extracted_handwritten_text?.text?.trim()?.length === 0)
        )
        ?.slice(0, 10);

      setMetrics({
        totalImages,
        totalProcessed,
        notProcessed,
        processingRate: totalImages > 0 ? (totalProcessed / totalImages * 100) : 0,
        textFound: withText.length,
        noTextFound: noTextFound.length,
        textFoundRate: totalProcessed > 0 ? (withText.length / totalProcessed * 100) : 0,
        avgConfidence: avgConfidence * 100,
        qualityDistribution,
        languageDistribution,
        avgProcessingTime,
      });

      setFailedExtractions(failed);
    } catch (error) {
      console.error('Error fetching OCR metrics:', error);
      toast?.error('Failed to load OCR metrics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3]?.map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">OCR Performance Metrics</h2>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e?.target?.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <FileText className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-blue-900">{metrics?.totalProcessed}</span>
            </div>
            <p className="text-sm text-blue-700 mt-2">Images Scanned</p>
            <p className="text-xs text-blue-600 mt-1">
              {metrics?.processingRate?.toFixed(1)}% of {metrics?.totalImages} uploaded
              {metrics?.notProcessed > 0 && (
                <span className="text-blue-400"> ({metrics?.notProcessed} pending)</span>
              )}
            </p>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-green-900">{metrics?.textFound}</span>
            </div>
            <p className="text-sm text-green-700 mt-2">Text Extracted</p>
            <p className="text-xs text-green-600 mt-1">
              {metrics?.textFoundRate?.toFixed(1)}% of scanned images had handwriting
            </p>
          </div>

          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <TrendingUp className="w-8 h-8 text-yellow-600" />
              <span className="text-2xl font-bold text-yellow-900">{metrics?.avgConfidence?.toFixed(1)}%</span>
            </div>
            <p className="text-sm text-yellow-700 mt-2">Avg Confidence</p>
            <p className="text-xs text-yellow-600 mt-1">
              Avg time: {(metrics?.avgProcessingTime / 1000)?.toFixed(1)}s per image
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <XCircle className="w-8 h-8 text-gray-500" />
              <span className="text-2xl font-bold text-gray-700">{metrics?.noTextFound}</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">No Handwriting Found</p>
            <p className="text-xs text-gray-500 mt-1">
              Scanned but contained no handwritten text
            </p>
          </div>
        </div>

        {/* Quality Distribution */}
        <div className="border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Quality Distribution</h3>
          <div className="space-y-2">
            {Object.entries(metrics?.qualityDistribution || {})?.map(([quality, count]) => {
              const percentage = metrics?.totalProcessed > 0 ? (count / metrics?.totalProcessed * 100) : 0;
              const colorClass = quality === 'good' ? 'bg-green-500' : quality === 'fair' ? 'bg-yellow-500' : 'bg-red-500';
              
              return (
                <div key={quality}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-700 capitalize">{quality}</span>
                    <span className="text-gray-900 font-medium">{count} ({percentage?.toFixed(1)}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className={`${colorClass} h-2 rounded-full`} style={{ width: `${percentage}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Language Distribution */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-5 h-5 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Language Distribution</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(metrics?.languageDistribution || {})?.map(([lang, count]) => (
              <span key={lang} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {lang?.toUpperCase()}: {count}
              </span>
            ))}
          </div>
        </div>

        {/* Failed Extractions */}
        {failedExtractions?.length > 0 && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-700">Images with No Handwriting Detected</h3>
            </div>
            <div className="space-y-2">
              {failedExtractions?.map((upload) => (
                <div key={upload?.id} className="bg-white border border-gray-200 rounded p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{upload?.file_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Case ID: {upload?.case_id?.substring(0, 8)}... | {new Date(upload?.uploaded_at)?.toLocaleDateString()}
                      </p>
                    </div>
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">No handwriting</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OCRMetricsPanel;
